/**
 * Authentication Context
 * 
 * Provides authentication state management for the application.
 * Handles session validation, login/logout, and protected routes.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

/**
 * User information from session
 */
export interface User {
  userId: string;
  email: string;
  emailVerified: boolean;
  sessionCreatedAt: string;
  sessionExpiresAt: string;
}

/**
 * Authentication context state
 */
interface AuthContextState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  sessionExpired: boolean;
  error: string | null;
  login: (email: string) => Promise<{ challengeId: string }>;
  verifyOtp: (email: string, otp: string, challengeId: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  clearSessionExpired: () => void;
}

const AuthContext = createContext<AuthContextState | undefined>(undefined);

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Authentication Provider Component
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  /**
   * Check authentication status on mount
   */
  const refreshAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setSessionExpired(false);
      } else if (response.status === 401) {
        // If user was previously authenticated, this is a session expiry
        if (user) {
          setSessionExpired(true);
        }
        setUser(null);
      } else {
        console.error('Auth check failed:', response.status);
        setUser(null);
      }
    } catch (err) {
      console.error('Auth check error:', err);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Initialize auth state on mount
   */
  useEffect(() => {
    void refreshAuth();
  }, [refreshAuth]);

  /**
   * Initiate login with email (Cognito OTP)
   */
  const login = async (email: string): Promise<{ challengeId: string }> => {
    setError(null);

    const response = await fetch(`${API_BASE_URL}/api/auth/start`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const data = await response.json();
      const errorMessage = data.error || 'Failed to send verification code';
      setError(errorMessage);
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return { challengeId: data.challengeId };
  };

  /**
   * Verify OTP code (Cognito authentication)
   */
  const verifyOtp = async (email: string, otp: string, challengeId: string): Promise<void> => {
    setError(null);

    const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, otp, challengeId }),
    });

    if (!response.ok) {
      const data = await response.json();
      const errorMessage = data.error || 'Invalid verification code';
      setError(errorMessage);
      throw new Error(errorMessage);
    }

    const data = await response.json();
    setUser({
      userId: data.userId,
      email: data.email,
      emailVerified: data.emailVerified,
      sessionCreatedAt: new Date().toISOString(),
      sessionExpiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    });
  };

  /**
   * Logout user
   */
  const logout = async (): Promise<void> => {
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
    }
  };

  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  const value: AuthContextState = {
    user,
    isLoading,
    isAuthenticated: !!user,
    sessionExpired,
    error,
    login,
    verifyOtp,
    logout,
    refreshAuth,
    clearSessionExpired,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access authentication context
 */
export function useAuth(): AuthContextState {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook to require authentication
 * Returns user if authenticated, redirects to login if not
 */
export function useRequireAuth(): User {
  const { user, isLoading, isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Redirect to login page
      window.location.href = '/login';
    }
  }, [isLoading, isAuthenticated]);

  if (!user) {
    throw new Error('User not authenticated');
  }

  return user;
}
